<template>
  <v-main>
    <v-container fluid>
      <v-toolbar class="elevation-0">
        <v-btn icon :to="{ name: 'projecaoFinanceira' }" class="mr-2">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="pl-0">
          {{ input.nomeTurma || (isNova ? 'Nova Projeção' : 'Projeção sem nome') }}
          <v-chip v-if="isNova" x-small color="warning" class="ml-2">não salva</v-chip>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!input.nomeTurma"
          @click="salvar"
        >
          <v-icon left>mdi-content-save</v-icon> {{ isNova ? 'Salvar projeção' : 'Salvar alterações' }}
        </v-btn>
      </v-toolbar>

      <v-alert v-if="!input.nomeTurma" type="info" outlined dense class="mb-0">
        Informe o <strong>Nome da Turma</strong> na aba "Projeção Heppi" para poder salvar esta projeção.
      </v-alert>

      <v-tabs v-model="activeTab" background-color="transparent" color="primary" show-arrows>
        <v-tab>Projeção Heppi</v-tab>
        <v-tab>Outras Receitas</v-tab>
        <v-tab>Saídas Estratégicas</v-tab>
        <v-tab>Visualização Turma</v-tab>
        <v-tab>Visualização Formando</v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-tabs-items v-model="activeTab" class="transparent pt-4">
        <v-tab-item>
          <tab-projecao-heppi :input="input" :kpis="kpis" @update="onInputUpdate"></tab-projecao-heppi>
        </v-tab-item>
        <v-tab-item>
          <tab-outras-receitas :items="outrasReceitas" @update="outrasReceitas = $event"></tab-outras-receitas>
        </v-tab-item>
        <v-tab-item>
          <tab-saidas-estrategicas
            :saidas="saidas"
            :data-inicio="input.dataInicio"
            :horizonte-meses="input.horizonteMeses"
            @update="saidas = $event"
            @change="recalcular"
          ></tab-saidas-estrategicas>
        </v-tab-item>
        <v-tab-item>
          <tab-visualizacao-turma :results="results" :semesters="semesters"></tab-visualizacao-turma>
        </v-tab-item>
        <v-tab-item>
          <tab-visualizacao-formando :results="results" :kpis="kpis" :quantidade-alunos="input.quantidadeAlunos"></tab-visualizacao-formando>
        </v-tab-item>
      </v-tabs-items>
    </v-container>
  </v-main>
</template>

<script>
import { mapMutations } from 'vuex'
import TabProjecaoHeppi from './TabProjecaoHeppi'
import TabOutrasReceitas from './TabOutrasReceitas'
import TabSaidasEstrategicas from './TabSaidasEstrategicas'
import TabVisualizacaoTurma from './TabVisualizacaoTurma'
import TabVisualizacaoFormando from './TabVisualizacaoFormando'
import { calculateProjection, calculateKpis, groupBySemester } from './projecaoEngine'
import { getProjecao, createProjecao, updateProjecao } from './projecaoStorage'

function defaultInput () {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return {
    nomeTurma: '',
    quantidadeAlunos: 50,
    mensalidade: 584.38,
    dataInicio: `${now.getFullYear()}-${month}-01`,
    taxaHeppi: 7,
    taxaRendimentoMensal: 1,
    aliquotaIR: 17.5,
    horizonteMeses: 66
  }
}

export default {
  name: 'ProjecaoFinanceira',
  components: {
    TabProjecaoHeppi,
    TabOutrasReceitas,
    TabSaidasEstrategicas,
    TabVisualizacaoTurma,
    TabVisualizacaoFormando
  },
  props: {
    id: { type: String, default: null }
  },
  data: () => ({
    activeTab: 0,
    saving: false,
    input: defaultInput(),
    saidas: [],
    outrasReceitas: [],
    results: [],
    kpis: {},
    semesters: []
  }),
  computed: {
    isNova () {
      return !this.id
    }
  },
  watch: {
    input: {
      deep: true,
      handler () {
        this.recalcular()
      }
    }
  },
  methods: {
    ...mapMutations(['SET_SNACKBAR']),
    onInputUpdate (newInput) {
      this.input = newInput
    },
    recalcular () {
      const engineInput = {
        ...this.input,
        saidas: this.saidas
          .filter(s => s.ativado)
          .map(s => ({ mes: s.mes, valor: s.valor, descricao: s.descricao }))
      }
      this.results = calculateProjection(engineInput)
      this.kpis = calculateKpis(this.results, engineInput)
      this.semesters = groupBySemester(this.results)
    },
    carregar () {
      if (!this.id) {
        this.recalcular()
        return
      }
      getProjecao(this.id)
        .then(projecao => {
          if (!projecao) {
            this.SET_SNACKBAR({ text: 'Projeção não encontrada.' })
            this.$router.replace({ name: 'projecaoFinanceira' })
            return
          }
          this.input = { ...defaultInput(), ...projecao.input }
          this.saidas = projecao.saidas || []
          this.outrasReceitas = projecao.outrasReceitas || []
          this.recalcular()
        })
        .catch(() => {
          this.SET_SNACKBAR({ text: 'Houve um erro ao carregar a projeção.' })
        })
    },
    payload () {
      return {
        nomeTurma: this.input.nomeTurma,
        input: { ...this.input },
        saidas: this.saidas,
        outrasReceitas: this.outrasReceitas,
        kpis: {
          saldoFinal: this.kpis.saldoFinal || 0,
          lucroProjetado: this.kpis.lucroProjetado || 0,
          receitaTotal: this.kpis.receitaTotal || 0,
          roi: this.kpis.roi || 0
        }
      }
    },
    salvar () {
      if (!this.input.nomeTurma) return
      this.saving = true
      if (this.id) {
        updateProjecao(this.id, this.payload())
          .then(() => {
            this.SET_SNACKBAR({ text: 'Projeção salva' })
            this.saving = false
          })
          .catch(() => {
            this.SET_SNACKBAR({ text: 'Houve um erro ao salvar a projeção.' })
            this.saving = false
          })
      } else {
        createProjecao(this.payload())
          .then(newId => {
            this.SET_SNACKBAR({ text: 'Projeção criada' })
            this.saving = false
            this.$router.replace({ name: 'projecaoFinanceiraDetalhe', params: { id: newId } })
          })
          .catch(() => {
            this.SET_SNACKBAR({ text: 'Houve um erro ao criar a projeção.' })
            this.saving = false
          })
      }
    }
  },
  created () {
    this.carregar()
  }
}
</script>
